'use client';
import React from 'react';
import {
  TOOLTIP_DEMO_DATA,
  POPOVER_DEMO_DATA,
  Tooltip,
  type TooltipPlacement,
  Popover,
  type PopoverPlacement,
  Card,
  Button,
  Heading,
  useToast,
  type ToastType,
} from '@ui';
import { withOptionalId } from '@lib/withOptionalId';
import { Section, Container } from '@layout';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';

interface TooltipsDemoProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
}

const TooltipsDemo: React.FC<TooltipsDemoProps> = ({
  isSpanish = false,
  sectionID = '',
  classes = '',
  containerId,
  containerClasses,
}) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);
  const placement: PopoverPlacement = 'top-end';

  const tooltipDemoData = isSpanish
    ? [
        {
          type: 'button',
          label: 'Pasa el mouse o enfócalo',
          tooltip: (
            <>
              Desde arriba
              <br />
              tooltip-top
            </>
          ),
        },
        {
          type: 'link',
          label: 'Enlace con tooltip',
          tooltip: (
            <>
              Desde arriba
              <br />
              tooltip-top
            </>
          ),
          href: '#',
        },
        {
          type: 'chip',
          label: 'Chip hover o foco',
          tooltip: (
            <>
              Desde arriba
              <br />
              tooltip-top
            </>
          ),
        },
        {
          type: 'button',
          label: 'Abajo inicio',
          tooltip: (
            <>
              Desde abajo inicio
              <br />
              tooltip-bottom-start
            </>
          ),
          placement: 'bottom-start',
        },
        {
          type: 'button',
          label: 'Abajo fin',
          tooltip: (
            <>
              Desde abajo fin
              <br />
              tooltip-bottom-end
            </>
          ),
          placement: 'bottom-end',
        },
        {
          type: 'button',
          label: 'Arriba inicio',
          tooltip: (
            <>
              Desde arriba inicio
              <br />
              tooltip-top-start
            </>
          ),
          placement: 'top-start',
        },
        {
          type: 'button',
          label: 'Arriba fin',
          tooltip: (
            <>
              Desde arriba fin
              <br />
              tooltip-top-end
            </>
          ),
          placement: 'top-end',
        },
        {
          type: 'button',
          label: 'Izquierda',
          tooltip: (
            <>
              Desde la izquierda
              <br />
              tooltip-left
            </>
          ),
          placement: 'left',
        },
        {
          type: 'button',
          label: 'Derecha',
          tooltip: (
            <>
              Desde la derecha
              <br />
              tooltip-right
            </>
          ),
          placement: 'right',
        },
      ]
    : TOOLTIP_DEMO_DATA;

  const popoverDemoData = isSpanish
    ? [
        {
          trigger: {
            label: 'Popover superior',
            variant: 'ghost',
            placement: 'top',
            className: 'btn-ghost',
          },
          body: 'Los popovers son paneles anclados y descartables para interacciones ligeras. Este se ancla a la parte superior del elemento activador.',
        },
        {
          trigger: {
            label: 'Popover inferior',
            variant: 'ghost',
            placement: 'bottom',
            className: 'btn-ghost',
          },
          body: 'Los popovers son paneles anclados y descartables para interacciones ligeras. Este se ancla a la parte inferior del elemento activador.',
        },
        {
          trigger: {
            label: 'Popover izquierdo',
            variant: 'ghost',
            placement: 'left',
            className: 'btn-ghost',
          },
          body: 'Los popovers son paneles anclados y descartables para interacciones ligeras. Este se ancla a la izquierda del elemento activador.',
        },
        {
          trigger: {
            label: 'Popover derecho',
            variant: 'ghost',
            placement: 'right',
            className: 'btn-ghost',
          },
          body: 'Los popovers son paneles anclados y descartables para interacciones ligeras. Este se ancla a la derecha del elemento activador.',
        },
        {
          trigger: {
            label: 'Popover arriba inicio',
            variant: 'ghost',
            placement: 'top-start',
            className: 'btn-ghost',
          },
          body: 'Los popovers son paneles anclados y descartables para interacciones ligeras. Este se ancla arriba e inicia (top-start) del elemento activador.',
        },
        {
          trigger: {
            label: 'Popover arriba fin',
            variant: 'ghost',
            placement: 'top-end',
            className: 'btn-ghost',
          },
          body: 'Los popovers son paneles anclados y descartables para interacciones ligeras. Este se ancla arriba a fin (top-end) del elemento activador.',
        },
        {
          trigger: {
            label: 'Popover abajo inicio',
            variant: 'ghost',
            placement: 'bottom-start',
            className: 'btn-ghost',
          },
          body: 'Los popovers son paneles anclados y descartables para interacciones ligeras. Este se ancla abajo a inicio (bottom-start) del elemento activador.',
        },
        {
          trigger: {
            label: 'Popover abajo fin',
            variant: 'ghost',
            placement: 'bottom-end',
            className: 'btn-ghost',
          },
          body: 'Los popovers son paneles anclados y descartables para interacciones ligeras. Este se ancla abajo a fin (bottom-end) del elemento activador.',
        },
      ]
    : POPOVER_DEMO_DATA;

  const handleCancel = () => {
    // demo-only; no-op by default
  };

  const handleConfirm = () => {
    // demo-only; no-op by default
  };

  // Toast bridge: prefer the React hook if available, otherwise call
  // window.Toast or dispatch a document event that the provider listens for.
  let showToastBridge: ((message: string, type?: string, header?: string) => void) | null = null;
  try {
    const ctx = useToast();
    showToastBridge = (message: string, type: string = 'info', header?: string) =>
      ctx.showToast(message, type as ToastType, header);
  } catch {
    // no provider in this render tree; we'll fallback to window.Toast or event
    showToastBridge = (message: string, type = 'info', header?: string) => {
      if (typeof window !== 'undefined') {
        const maybeToast = window as unknown as {
          Toast?: (o: { type?: string; title?: string; message?: string }) => void;
        };
        if (maybeToast.Toast) {
          try {
            maybeToast.Toast({ type, title: header, message });
            return;
          } catch {
            // ignore and continue to event fallback
          }
        }
      }
      // dispatch custom event that the React provider registers for
      if (typeof document !== 'undefined') {
        document.dispatchEvent(
          new CustomEvent('ui-show-toast', {
            detail: { message, type, header },
          })
        );
      }
    };
  }

  return (
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading as='h3'>{t('Tooltips & Popovers', 'Tooltips y Popovers')}</Heading>
        <p className='sub full'>
          {t(
            'Short hints (hover/focus) and richer, non-modal popovers (click/ESC/outside).',
            'Pistas breves (hover/foco) y popovers mas completos, no modales (clic/ESC/afuera).'
          )}
        </p>

        <Card className='mb-16'>
          <div className='card-header'>
            <Heading as='h4'>{t('Tooltips', 'Tooltips')}</Heading>
          </div>
          <div className='row btn-row'>
            {tooltipDemoData.map((item, i) => {
              if (item.type === 'button') {
                return (
                  <Tooltip
                    key={i}
                    content={item.tooltip}
                    placement={item.placement as TooltipPlacement}
                  >
                    <Button
                      label={item.label}
                      className='btn-sm btn-ghost'
                      as='button'
                      type='button'
                    />
                  </Tooltip>
                );
              }
              if (item.type === 'link') {
                return (
                  <Tooltip
                    key={i}
                    content={item.tooltip}
                    placement={item.placement as TooltipPlacement}
                  >
                    <Button label={item.label} className='btn-sm' as='a' href={item.href || '#'} />
                  </Tooltip>
                );
              }
              if (item.type === 'chip') {
                return (
                  <Tooltip
                    key={i}
                    content={item.tooltip}
                    placement={item.placement as TooltipPlacement}
                  >
                    <span className='chip-md' tabIndex={0}>
                      {item.label}
                    </span>
                  </Tooltip>
                );
              }
              return null;
            })}
          </div>
        </Card>

        <Card className='mb-16'>
          <div className='card-header'>
            <Heading as='h4'>{t('Popovers', 'Popovers')}</Heading>
          </div>
          <div className='row btn-row'>
            {popoverDemoData.map((item, i) => (
              <Popover
                key={i}
                trigger={item.trigger.label}
                placement={item.trigger.placement as PopoverPlacement}
                header={item.header}
                popoverType={item.trigger.popoverType}
                className={item.trigger.className}
                triggerClassName={item.trigger.className}
              >
                {Array.isArray(item.body) ? (
                  item.body.map((b, j) =>
                    b.type === 'link' ? (
                      <Button
                        key={j}
                        label={b.label}
                        className='popover-item'
                        as='a'
                        href={b.href || '#'}
                      />
                    ) : null
                  )
                ) : (
                  <p className='sub full'>{item.body}</p>
                )}
              </Popover>
            ))}
          </div>
        </Card>
        <Card>
          <div className='card-header mt-24'>
            <Heading as='h4'>{t('Popovers Examples', 'Ejemplos de Popovers')}</Heading>
          </div>
          <div className='row btn-row'>
            <Popover
              trigger={t('Popover Actions', 'Acciones de popover')}
              placement='bottom-start'
              header={t('Quick actions', 'Acciones rapidas')}
              popoverType='actions-popover'
              className='btn-secondary'
              triggerClassName='btn-secondary'
            >
              <Button
                label={t('Create milestone', 'Crear hito')}
                className='popover-item'
                as='a'
                href='#'
              />
              <Button
                label={t('Invite site user', 'Invitar usuario del sitio')}
                className='popover-item'
                as='a'
                href='#'
              />
              <Button
                label={t('Export report', 'Exportar reporte')}
                className='popover-item'
                as='a'
                href='#'
              />
            </Popover>
            <Popover
              trigger={t('Delete Popover', 'Popover de eliminar')}
              placement={placement}
              triggerClassName='btn btn-danger'
              showCloseButton={false}
            >
              <div className='popconfirm-body'>
                <b>{t('Delete this record?', 'Eliminar este registro?')}</b>
                <p className='hint'>
                  {t('This action cannot be undone.', 'Esta accion no se puede deshacer.')}
                </p>
                <div className='row mt-12' style={{ justifyContent: 'flex-end' }}>
                  <Button
                    className='btn-ghost btn-sm'
                    data-cancel
                    as='button'
                    onClick={() => {
                      handleCancel();
                    }}
                    label={t('Cancel', 'Cancelar')}
                  />
                  <Button
                    className='btn-danger btn-sm'
                    data-confirm
                    as='button'
                    onClick={() => {
                      handleConfirm();
                      if (showToastBridge) {
                        showToastBridge(
                          t('Delete record processed.', 'Eliminacion de registro procesada.'),
                          'success',
                          t('Confirmed', 'Confirmado')
                        );
                      }
                    }}
                    label={t('Delete', 'Eliminar')}
                  />
                </div>
              </div>
            </Popover>
            <Popover
              trigger={t('Warning Popover', 'Popover de advertencia')}
              placement={placement}
              triggerClassName='btn btn-warning'
              showCloseButton={false}
            >
              <div className='popconfirm-body'>
                <b>{t('Archive this?', 'Archivar esto?')}</b>
                <p className='hint'>
                  {t(
                    'You can restore it later from Settings.',
                    'Puedes restaurarlo despues desde Configuracion.'
                  )}
                </p>
                <div className='row mt-12' style={{ justifyContent: 'flex-end' }}>
                  <Button
                    className='btn-ghost btn-sm'
                    data-cancel
                    as='button'
                    onClick={() => {
                      handleCancel();
                    }}
                    label={t('Cancel', 'Cancelar')}
                  />
                  <Button
                    className='btn-warning btn-sm'
                    data-confirm
                    as='button'
                    onClick={() => {
                      handleConfirm();
                      if (showToastBridge) {
                        showToastBridge(
                          t('Archive study processed.', 'Archivado de estudio procesado.'),
                          'success',
                          t('Confirmed', 'Confirmado')
                        );
                      }
                    }}
                    label={t('Archive', 'Archivar')}
                  />
                </div>
              </div>
            </Popover>
          </div>
        </Card>
      <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<button class="btn btn-ghost tooltip-trigger" type="button">Hover</button>
<div class="tooltip tooltip-top is-show" role="tooltip">Helpful hint</div>

<button class="btn popover-trigger" type="button">Open</button>
<div class="popover popover-bottom">
  <div class="popover-header">Title</div>
  <div class="popover-body">More detail.</div>
</div>`}
        />
      </Container>
    </Section>
  );
};

export default TooltipsDemo;
